import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaCashRegister, FaLock, FaUser, FaDollarSign, FaExclamationTriangle } from 'react-icons/fa';
import instanciaAxios, { esErrorPermiso } from '../../miscelaneo/instanciaAxios';
import { useContextoSesion } from '../../miscelaneo/ContextoSesion';
import SpinnerCargando from '../util/SpinnerCargando';

export default function AperturaCaja() {
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || '/Dashboard';
  const { setSesionCaja, verificarSesionCaja, sesionCaja, sesionCajaCargada, cargando: cargandoContexto, tienePermiso } = useContextoSesion();

  const [cajas, setCajas] = useState([]);
  const [cajaSeleccionada, setCajaSeleccionada] = useState(null);
  const [montoApertura, setMontoApertura] = useState('');
  const [comentario, setComentario] = useState('');
  const [cargando, setCargando] = useState(true);
  const [abriendo, setAbriendo] = useState(false);

  // Cierre forzado
  const [modalCierreForzado, setModalCierreForzado] = useState(null); // caja object or null
  const [comentarioCierreForzado, setComentarioCierreForzado] = useState('');
  const [cerrandoForzado, setCerrandoForzado] = useState(false);

  // Si ya tiene caja abierta, redirigir (solo después de verificar)
  useEffect(() => {
    if (!sesionCajaCargada || cargandoContexto) return;
    if (sesionCaja) {
      toast.info(`Ya tienes una caja abierta: ${sesionCaja.nombre_caja}`);
      navigate(from);
    }
  }, [sesionCaja, sesionCajaCargada, cargandoContexto, navigate, from]);

  // Cargar cajas disponibles
  useEffect(() => {
    cargarCajas();
  }, []);

  const cargarCajas = async () => {
    try {
      setCargando(true);
      const response = await instanciaAxios.get('/sesion-caja/cajas-disponibles');
      setCajas(response.data);
    } catch (error) {
      if (esErrorPermiso(error)) return;
      console.error('Error al cargar cajas:', error);
      toast.error('Error al cargar las cajas disponibles');
    } finally {
      setCargando(false);
    }
  };

  const seleccionarCaja = (caja) => {
    if (caja.sesion_abierta_id) {
      toast.warning(`Esta caja está siendo usada por ${caja.usuario_nombre} ${caja.usuario_apellido || ''}`);
      return;
    }
    setCajaSeleccionada(caja);
    // Si hay fondo del cierre anterior, pre-llenar
    if (caja.fondo_siguiente && parseFloat(caja.fondo_siguiente) > 0) {
      setMontoApertura(parseFloat(caja.fondo_siguiente).toString());
    } else {
      setMontoApertura('0');
    }
  };

  const abrirCaja = async () => {
    if (!cajaSeleccionada) {
      toast.warning('Selecciona una caja');
      return;
    }

    try {
      setAbriendo(true);
      const response = await instanciaAxios.post('/sesion-caja/abrir', {
        id_cuenta_tesoreria: cajaSeleccionada.id_cuenta_tesoreria,
        monto_apertura: parseFloat(montoApertura) || 0,
        comentario_apertura: comentario || null
      });

      toast.success(response.data.mensaje);
      setSesionCaja(response.data.sesion);
      navigate(from);
    } catch (error) {
      if (esErrorPermiso(error)) return;
      console.error('Error al abrir caja:', error);
      toast.error(error.response?.data?.mensaje || 'Error al abrir la caja');
    } finally {
      setAbriendo(false);
    }
  };

  const ejecutarCierreForzado = async () => {
    if (!modalCierreForzado?.sesion_abierta_id) return;
    if (!comentarioCierreForzado.trim()) {
      toast.warning('Debes ingresar una razón para el cierre forzado');
      return;
    }

    try {
      setCerrandoForzado(true);
      await instanciaAxios.post(`/sesion-caja/${modalCierreForzado.sesion_abierta_id}/cierre-forzado`, {
        comentario_cierre: comentarioCierreForzado.trim()
      });

      toast.success('Caja cerrada forzosamente. Ahora está disponible.');
      setModalCierreForzado(null);
      setComentarioCierreForzado('');
      await cargarCajas();
    } catch (error) {
      if (esErrorPermiso(error)) return;
      console.error('Error en cierre forzado:', error);
      toast.error(error.response?.data?.mensaje || 'Error al realizar cierre forzado');
    } finally {
      setCerrandoForzado(false);
    }
  };

  if (cargando) return <SpinnerCargando />;

  const puedeHacerCierreForzado = tienePermiso('caja_cierre_forzado');

  return (
    <div className="max-w-2xl mx-auto p-4">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-md p-6 mb-6 border border-gray-200">
        <div className="flex items-center gap-3 mb-2">
          <div className="bg-green-100 p-3 rounded-full">
            <FaCashRegister className="text-green-600 text-2xl" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Apertura de Caja</h1>
            <p className="text-gray-500 text-sm">Selecciona una caja y confirma el fondo inicial</p>
          </div>
        </div>
      </div>

      {/* Lista de cajas disponibles */}
      <div className="bg-white rounded-2xl shadow-md p-6 mb-6 border border-gray-200">
        <h2 className="text-lg font-semibold text-gray-700 mb-4">Cajas Disponibles</h2>
        
        {cajas.length === 0 ? (
          <p className="text-gray-500 text-center py-8">
            No hay cajas configuradas. Contacte al administrador.
          </p>
        ) : (
          <div className="grid gap-3">
            {cajas.map((caja) => {
              const ocupada = !!caja.sesion_abierta_id;
              const seleccionada = cajaSeleccionada?.id_cuenta_tesoreria === caja.id_cuenta_tesoreria;

              return (
                <div
                  key={caja.id_cuenta_tesoreria}
                  className={`rounded-xl border-2 transition-all duration-200
                    ${seleccionada 
                      ? 'border-green-500 bg-green-50 shadow-md' 
                      : ocupada 
                        ? 'border-gray-200 bg-gray-100 opacity-80' 
                        : 'border-gray-200 hover:border-green-300 hover:bg-green-50'
                    }`}
                >
                  <button
                    onClick={() => !ocupada && seleccionarCaja(caja)}
                    disabled={ocupada}
                    className={`flex items-center justify-between p-4 w-full text-left
                      ${ocupada ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-full ${ocupada ? 'bg-red-100' : seleccionada ? 'bg-green-100' : 'bg-gray-100'}`}>
                        {ocupada ? (
                          <FaLock className="text-red-500" />
                        ) : (
                          <FaCashRegister className={seleccionada ? 'text-green-600' : 'text-gray-500'} />
                        )}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-800">{caja.descripcion}</p>
                        {ocupada && (
                          <p className="text-xs text-red-500 flex items-center gap-1">
                            <FaUser className="text-xs" />
                            En uso por {caja.usuario_nombre} {caja.usuario_apellido || ''}
                          </p>
                        )}
                        {!ocupada && caja.fondo_siguiente && parseFloat(caja.fondo_siguiente) > 0 && (
                          <p className="text-xs text-green-600">
                            Fondo del cierre anterior: RD$ {parseFloat(caja.fondo_siguiente).toLocaleString('es-DO', { minimumFractionDigits: 2 })}
                          </p>
                        )}
                      </div>
                    </div>
                    {seleccionada && (
                      <span className="text-green-600 font-bold text-sm">Seleccionada ✓</span>
                    )}
                  </button>

                  {/* Botón de cierre forzado para cajas ocupadas */}
                  {ocupada && puedeHacerCierreForzado && (
                    <div className="px-4 pb-3 pt-0">
                      <button
                        onClick={() => {
                          setModalCierreForzado(caja);
                          setComentarioCierreForzado('');
                        }}
                        className="w-full px-3 py-2 bg-red-50 text-red-700 border border-red-200 rounded-lg text-sm font-medium hover:bg-red-100 transition-colors flex items-center justify-center gap-2"
                      >
                        <FaExclamationTriangle className="text-xs" />
                        Cerrar Forzosamente
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Formulario de apertura (solo si hay caja seleccionada) */}
      {cajaSeleccionada && (
        <div className="bg-white rounded-2xl shadow-md p-6 mb-6 border border-gray-200">
          <h2 className="text-lg font-semibold text-gray-700 mb-4">Datos de Apertura</h2>

          <div className="space-y-4">
            {/* Monto de apertura */}
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                <FaDollarSign className="inline mr-1" />
                Monto de Apertura (Fondo Inicial)
              </label>
              <input
                type="number"
                value={montoApertura}
                onChange={(e) => setMontoApertura(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 text-lg"
                placeholder="0.00"
                min="0"
                step="0.01"
              />
              {cajaSeleccionada.fondo_siguiente && parseFloat(cajaSeleccionada.fondo_siguiente) > 0 && (
                <p className="text-xs text-gray-500 mt-1">
                  Fondo sugerido del cierre anterior: RD$ {parseFloat(cajaSeleccionada.fondo_siguiente).toLocaleString('es-DO', { minimumFractionDigits: 2 })}
                </p>
              )}
            </div>

            {/* Comentario */}
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Comentario (opcional)
              </label>
              <textarea
                value={comentario}
                onChange={(e) => setComentario(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500"
                rows="2"
                placeholder="Comentario de apertura..."
              />
            </div>
          </div>

          {/* Botón de apertura */}
          <button
            onClick={abrirCaja}
            disabled={abriendo}
            className="w-full mt-6 px-6 py-4 bg-green-600 text-white rounded-xl font-semibold text-lg hover:bg-green-700 transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {abriendo ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                Abriendo...
              </>
            ) : (
              <>
                <FaCashRegister />
                Abrir Caja
              </>
            )}
          </button>
        </div>
      )}

      {/* Modal de Cierre Forzado */}
      {modalCierreForzado && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-md mx-4 border border-red-200">
            {/* Header */}
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-red-100 p-3 rounded-full">
                <FaExclamationTriangle className="text-red-600 text-xl" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-800">Cierre Forzado de Caja</h3>
                <p className="text-sm text-gray-500">Esta acción no se puede deshacer</p>
              </div>
            </div>

            {/* Info de la caja */}
            <div className="bg-red-50 border border-red-100 rounded-xl p-4 mb-4">
              <p className="text-sm font-medium text-gray-800">
                <span className="font-bold">{modalCierreForzado.descripcion}</span>
              </p>
              <p className="text-sm text-red-600 mt-1">
                En uso por: {modalCierreForzado.usuario_nombre} {modalCierreForzado.usuario_apellido || ''}
              </p>
              <p className="text-xs text-gray-500 mt-2">
                La caja se cerrará sin cuadre. El comentario quedará registrado con el prefijo [CIERRE FORZADO].
              </p>
            </div>

            {/* Campo de razón (obligatorio) */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Razón del cierre forzado <span className="text-red-500">*</span>
              </label>
              <textarea
                value={comentarioCierreForzado}
                onChange={(e) => setComentarioCierreForzado(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500"
                rows="3"
                placeholder="Ej: El usuario no cerró la caja al final del turno..."
                autoFocus
              />
            </div>

            {/* Botones */}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setModalCierreForzado(null);
                  setComentarioCierreForzado('');
                }}
                disabled={cerrandoForzado}
                className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={ejecutarCierreForzado}
                disabled={cerrandoForzado || !comentarioCierreForzado.trim()}
                className="flex-1 px-4 py-3 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {cerrandoForzado ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    Cerrando...
                  </>
                ) : (
                  <>
                    <FaExclamationTriangle className="text-sm" />
                    Confirmar Cierre
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
